// src/components/CashManagement/CollectionModal.jsx

import React, { useState, useEffect } from 'react';

const DENOMINATIONS = [5000, 1000, 500, 100, 50, 20, 10, 5, 2, 1];

const CollectionModal = ({ isOpen, onClose, onSave, type, selectedInventory, existingData }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    inventoryId: '',
    client: '',
    reference: '',
    description: '',
    salesPersonId: '',
    date: new Date().toISOString().split('T')[0],
    notes: DENOMINATIONS.map(denom => ({
      denomination: denom,
      quantity: 0
    }))
  });

  const [totalAmount, setTotalAmount] = useState(0);

  /* ✅ Auto-calculate total amount when notes change */
  useEffect(() => {
    const total = formData.notes.reduce((sum, note) => {
      return sum + (note.denomination * note.quantity);
    }, 0);
    setTotalAmount(total);
  }, [formData.notes]);

  /* ✅ Sync selected inventory from parent */
  useEffect(() => {
    if (selectedInventory && selectedInventory !== 'ALL') {
      setFormData(prev => ({
        ...prev,
        inventoryId: selectedInventory
      }));
    }
  }, [selectedInventory]);

  /* ✅ Load existing draft into form */
  useEffect(() => {
    if (isOpen && existingData) {
      setFormData({
        inventoryId: existingData.inventoryId || selectedInventory,
        client: existingData.client || '',
        reference: existingData.reference || '',
        description: existingData.description || '',
        salesPersonId: existingData.salesPersonId || '',
        date: existingData.date || new Date().toISOString().split('T')[0],
        notes: DENOMINATIONS.map(denom => {
          const found = existingData.notes?.find(n => n.denomination === denom);
          return {
            denomination: denom,
            quantity: found ? found.quantity : 0
          };
        })
      });
    }
  }, [isOpen, existingData, selectedInventory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (denomination, value) => {
    const quantity = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      notes: prev.notes.map(note =>
        note.denomination === denomination
          ? { ...note, quantity }
          : note
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.inventoryId || formData.inventoryId === 'ALL') {
      alert('Please select an inventory');
      return;
    }

    if (totalAmount === 0) {
      alert('Please enter at least one denomination quantity');
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        type: type,
        inventoryId: parseInt(formData.inventoryId),
        amount: totalAmount, // Use auto-calculated total
        date: formData.date,
        notes: formData.notes.filter(n => n.quantity > 0) // Only include notes with quantity
      };

      // Add salesPersonId for COLLECTION and CREDIT_COLLECTION types
      if ((type === 'COLLECTION' || type === 'CREDIT_COLLECTION') && formData.salesPersonId) {
        payload.salesPersonId = parseInt(formData.salesPersonId);
      }

      await onSave(payload);
      handleReset();
    } catch (error) {
      console.error('Error saving collection:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(prev => ({
      inventoryId: prev.inventoryId, // 🔒 Keep inventoryId
      date: new Date().toISOString().split('T')[0],
      notes: DENOMINATIONS.map(denom => ({
        denomination: denom,
        quantity: 0
      }))
    }));
  };

  const handleClose = () => {
    if (isSaving) return;
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  const showSalesPerson = type === 'COLLECTION' || type === 'CREDIT_COLLECTION';

  return (
    <div className="cash-modal-overlay" onClick={handleClose}>
      <div className="cash-modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="cash-modal-header">
          <h2>Add Collection</h2>
          <button className="cash-close-btn" onClick={handleClose} disabled={isSaving}>
            &times;
          </button>
        </div>

        <div className="cash-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              {/* Inventory (Locked) */}
              <div className="form-group">
                <label>Inventory *</label>
                <input
                  type="text"
                  value={formData.inventoryId}
                  disabled
                  className="locked-field"
                />
              </div>

             
            </div>

            {/* Denominations Grid */}
            <div className="denomination-grid">
              <h3>Cash Denominations</h3>
              {formData.notes.map((note) => (
                <div key={note.denomination} className="denomination-row">
                  <label>Rs. {note.denomination}</label>
                  <input
                    type="number"
                    min="0"
                    value={note.quantity}
                    onChange={(e) => handleQuantityChange(note.denomination, e.target.value)}
                    disabled={isSaving}
                    placeholder="Qty"
                  />
                  <span className="denomination-total">
                    Rs. {(note.denomination * note.quantity).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>

            {/* Total Amount Display */}
            <div className="total-amount">
              <strong>Total Amount: Rs. {totalAmount.toLocaleString()}</strong>
            </div>
          </form>
        </div>

        <div className="cash-modal-footer">
          <button className="cash-btn cash-btn-cancel" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="cash-btn cash-btn-save" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Collection'}
          </button>
        </div>
      </div>

      <style jsx>{`
        .large {
          max-width: 700px;
        }

        .locked-field {
          background: #f3f4f6;
          cursor: not-allowed;
        }

        .denomination-grid {
          margin-top: 20px;
          padding: 20px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .denomination-grid h3 {
          margin: 0 0 15px 0;
          font-size: 16px;
          color: #374151;
          font-weight: 600;
        }

        .denomination-row {
          display: grid;
          grid-template-columns: 120px 120px 1fr;
          align-items: center;
          gap: 15px;
          margin-bottom: 12px;
          padding: 10px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .denomination-row label {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }

        .denomination-row input {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .denomination-row input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .denomination-total {
          font-weight: 600;
          color: #059669;
          font-size: 14px;
          text-align: right;
        }

        .total-amount {
          margin-top: 20px;
          padding: 15px;
          background: #ecfdf5;
          border: 2px solid #10b981;
          border-radius: 8px;
          text-align: right;
        }

        .total-amount strong {
          font-size: 18px;
          color: #065f46;
        }
      `}</style>
    </div>
  );
};

export default CollectionModal;